import { memo, useCallback, useMemo, useState } from "react";
import type { components } from "../../../apis/gen";
import { VideoThumbNailCard } from "../../../inJoy-components/Card/VideoThumbNailCard";
import { VideoPlayerModal } from "../../../inJoy-components/Modal/VideoPlayerModal";
import {
	formatDurationMMSS,
	formatVideoTimestamp,
} from "../../../utils/date-utils";

type Event = components["schemas"]["Event"];
type Session = components["schemas"]["Session"];

const FALLBACK_POSTER = `${import.meta.env.VITE_APP_WEBSITE_URL}/img/event-page/game_placeholder_thumbnail.png`;

interface VideoData {
	id: string;
	poster: string;
	url: string;
	duration: string;
	title: string;
	timestamp: string;
	metadata?: string;
	sharelink?: string;
}

interface VideoTabContentProps {
	event: Event;
	selectedSessionIdx?: number | null;
}

const VideoSection = memo(function VideoSection({
	title,
	videos,
	onPlay,
}: {
	title: string;
	videos: VideoData[];
	onPlay: (video: VideoData) => void;
}) {
	return (
		<div className="flex flex-col gap-5 items-start py-5 w-full max-w-[1200px]">
			<p className="text-2xl font-medium text-color-Grey900 tracking-[-0.5px]">
				{title}
			</p>
			<div className="grid lg:grid-cols-3 gap-4 w-full">
				{videos.map((video) => (
					<VideoThumbNailCard
						key={video.id}
						title={video.title}
						poster={video.poster}
						videoUrl={video.url}
						duration={video.duration || undefined}
						timestamp={video.timestamp}
						metadata={video.metadata}
						sharelink={video.sharelink}
						onClick={() => onPlay(video)}
					/>
				))}
			</div>
		</div>
	);
});

/**
 * Transform sessions into full game video data
 */
function transformSessionsToFullGames(sessions: Session[]): VideoData[] {
	return sessions
		.map((session, index) => ({
			id: `game-${index}`,
			poster: FALLBACK_POSTER,
			url: session.playback_url || "",
			duration: formatDurationMMSS(session.started_at, session.ended_at),
			title: session.name || `Game ${index + 1}`,
			timestamp: formatVideoTimestamp(session.started_at),
			sharelink: session.sharelink || undefined,
		}))
		.filter((game) => game.url); // Only include sessions with playback URLs
}

/**
 * Transform sessions' highlights into highlight video data
 * @param sessions - Array of sessions
 * @param filterSessionIdx - Optional session index to filter highlights for a specific session
 */
function transformSessionsToHighlights(
	sessions: Session[],
	filterSessionIdx?: number | null,
): VideoData[] {
	const highlights: VideoData[] = [];

	sessions.forEach((session, sessionIndex) => {
		// Skip if filtering by session and this isn't the selected session
		if (filterSessionIdx !== null && filterSessionIdx !== undefined) {
			if (sessionIndex !== filterSessionIdx) return;
		}

		if (session.highlights && session.highlights.length > 0) {
			session.highlights.forEach((highlight, highlightIndex) => {
				highlights.push({
					id: `highlight-${sessionIndex}-${highlightIndex}`,
					poster: FALLBACK_POSTER,
					url: highlight.video_url || "",
					duration: "",
					title: highlight.name || `Highlight ${highlightIndex + 1}`,
					timestamp: formatVideoTimestamp(session.started_at),
					metadata: session.name,
					sharelink: session.sharelink || undefined,
				});
			});
		}
	});

	return highlights;
}

const VideoTabContent = memo(
	({ event, selectedSessionIdx }: VideoTabContentProps) => {
		const sessions = event.sessions || [];
		const isSessionView =
			selectedSessionIdx !== null && selectedSessionIdx !== undefined;

		const [activeVideo, setActiveVideo] = useState<{
			url: string;
			title: string;
			poster: string;
			sharelink?: string;
		} | null>(null);

		const handlePlayVideo = useCallback((video: VideoData) => {
			setActiveVideo({
				url: video.url,
				title: video.title,
				poster: video.poster,
				sharelink: video.sharelink,
			});
		}, []);

		const handleCloseModal = useCallback(() => {
			setActiveVideo(null);
		}, []);

		// Use stringified sessions array as a stable dependency if identity changes but content doesn't
		// Only include fields that actually affect the video display.
		// Ignore is_processing, result_details, etc.
		const _sessionsKey = useMemo(
			() =>
				JSON.stringify(
					sessions.map((s) => ({
						playback_url: s.playback_url,
						sharelink: s.sharelink,
						highlights: s.highlights?.map((h) => ({
							video_url: h.video_url,
							name: h.name,
						})),
					})),
				),
			[sessions],
		);

		// Memoize full games and highlights to prevent re-renders on every clock tick
		const fullGames = useMemo(() => {
			return isSessionView ? [] : transformSessionsToFullGames(sessions);
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [isSessionView, sessions]);

		const highlights = useMemo(() => {
			return transformSessionsToHighlights(sessions, selectedSessionIdx);
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [selectedSessionIdx, sessions]);

		const hasFullGames = fullGames.length > 0;
		const hasHighlights = highlights.length > 0;
		const hasNoVideos = !hasFullGames && !hasHighlights;

		return (
			<div className="bg-white relative shrink-0 w-full">
				<div className="flex flex-col items-center size-full">
					<div className="content-stretch flex flex-col items-center px-5 lg:py-4 relative w-full">
						<div className="content-stretch flex flex-col items-center relative shrink-0 w-full max-w-[1200px]">
							{hasNoVideos ? (
								<div className="my-5 lg:my-0 py-6 px-5 w-full h-[200px] lg:h-[284px] flex flex-col items-center justify-center gap-y-2 border border-color-Grey100 bg-color-Grey50 rounded">
									<p className="text-lg font-semibold text-color-Grey900">
										No video for this game yet
									</p>
									<p className="text-center text-sm text-color-Grey700">
										Game videos will be uploaded here after the match.
									</p>
								</div>
							) : (
								<>
									{hasFullGames && (
										<VideoSection
											title="Full Games"
											videos={fullGames}
											onPlay={handlePlayVideo}
										/>
									)}
									{hasHighlights && (
										<VideoSection
											title="Highlights"
											videos={highlights}
											onPlay={handlePlayVideo}
										/>
									)}
								</>
							)}
						</div>
					</div>
				</div>

				{/* Render a single VideoPlayerModal at the root of the tab */}
				<VideoPlayerModal
					isOpen={!!activeVideo}
					onClose={handleCloseModal}
					videoUrl={activeVideo?.url || ""}
					title={activeVideo?.title}
					poster={activeVideo?.poster}
					sharelink={activeVideo?.sharelink}
				/>
			</div>
		);
	},
);

VideoTabContent.displayName = "VideoTabContent";

export { VideoTabContent };
